import { useState, useCallback, useRef } from "react";
import type { ReactNode } from "react";
import { Popover } from "../Popover/Popover";
import { CopyIcon } from "@phosphor-icons/react/Copy";
import { CheckIcon } from "@phosphor-icons/react/Check";
import { copyToClipboard } from "../Feedback/copyToClipboard";
import { useCopyableMode } from "./Internal/CopyableContext";

/** Extract plain text from a ReactNode for clipboard copy */
function extractText(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node);
  // oxlint-disable-next-line fs/no-null -- null check required for ReactNode type
  if (node === undefined || node === null || typeof node === "boolean")
    return "";
  if (Array.isArray(node)) return node.map(extractText).join("");
  if (typeof node === "object" && "props" in node) {
    const element = node as { props: { children?: ReactNode } };
    if (element.props.children) return extractText(element.props.children);
  }
  return "";
}

export interface CopyOption {
  label: string;
  value: string;
}

interface CopyableValueProps {
  value: ReactNode;
  copyValue?: string | undefined;
  /** Multiple copy options shown when clicking the copy icon */
  copyOptions?: CopyOption[] | undefined;
  label?: string | undefined;
  /** Allow long values (URLs, tokens) to wrap onto multiple lines. Switches to a block-level layout with character-level breaking. */
  wrap?: boolean | undefined;
}

// oxlint-disable-next-line sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
export function CopyableValue({
  value,
  copyValue,
  copyOptions,
  label,
  wrap,
}: CopyableValueProps) {
  const mode = useCopyableMode();
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const textToCopy = copyValue ?? extractText(value);
  const hasOptions = copyOptions && copyOptions.length > 0;
  // A lone em-dash is the codebase's "no value" placeholder — never show a copy
  // affordance for it (copying "—" is meaningless).
  const isPlaceholder = textToCopy.trim() === "—";

  const handleCopy = useCallback(() => {
    if (!textToCopy) return;
    clearTimeout(copiedTimeoutRef.current);
    copyToClipboard(textToCopy, label);
    setCopied(true);
    copiedTimeoutRef.current = setTimeout(() => setCopied(false), 1500);
  }, [textToCopy, label]);

  if (!hasOptions && (!textToCopy || isPlaceholder)) return <>{value}</>;

  if (hasOptions) {
    return (
      <CopyWithOptions_
        value={value}
        copyOptions={copyOptions}
        label={label}
        mode={mode}
      />
    );
  }

  if (mode === "visible") {
    if (wrap) {
      return (
        <span
          role="button"
          tabIndex={0}
          onClick={handleCopy}
          onKeyDown={(e) => e.key === "Enter" && handleCopy()}
          className="group flex cursor-pointer items-start gap-1 rounded break-all transition-colors hover:text-blue-text"
        >
          {value}
          {copied ? (
            <CheckIcon
              className="mt-0.5 h-3 w-3 shrink-0 text-dark-green"
              weight="bold"
            />
          ) : (
            <CopyIcon className="mt-0.5 h-3 w-3 shrink-0 text-content-faint transition-colors group-hover:text-blue-text" />
          )}
        </span>
      );
    }
    return (
      <span
        role="button"
        tabIndex={0}
        onClick={handleCopy}
        onKeyDown={(e) => e.key === "Enter" && handleCopy()}
        className="group inline-flex cursor-pointer items-center gap-1 rounded transition-colors hover:text-blue-text"
      >
        {value}
        {copied ? (
          <CheckIcon className="h-3 w-3 text-dark-green" weight="bold" />
        ) : (
          <CopyIcon className="h-3 w-3 text-content-faint transition-colors group-hover:text-blue-text" />
        )}
      </span>
    );
  }

  if (wrap) {
    return (
      <span
        role="button"
        tabIndex={0}
        onClick={handleCopy}
        onKeyDown={(e) => e.key === "Enter" && handleCopy()}
        className="group flex cursor-pointer items-start gap-1 rounded break-all transition-colors hover:text-blue-text"
      >
        <span className="min-w-0 flex-1">{value}</span>
        <span className="mt-0.5 shrink-0 text-content-faint transition-colors group-hover:text-blue-text">
          {copied ? (
            <CheckIcon className="h-3 w-3 text-dark-green" weight="bold" />
          ) : (
            <CopyIcon className="h-3 w-3" />
          )}
        </span>
      </span>
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={handleCopy}
      onKeyDown={(e) => e.key === "Enter" && handleCopy()}
      className="group inline-flex cursor-pointer items-center gap-0.5 rounded transition-colors hover:text-blue-text"
    >
      <span className="transition-transform duration-150 group-hover:-translate-x-0.5">
        {value}
      </span>
      <span className="w-0 overflow-hidden opacity-0 transition-all duration-150 group-hover:w-4 group-hover:opacity-100">
        {copied ? (
          <CheckIcon className="h-3 w-3 text-dark-green" weight="bold" />
        ) : (
          <CopyIcon className="h-3 w-3" />
        )}
      </span>
    </span>
  );
}

function CopyWithOptions_({
  value,
  copyOptions,
  label,
  mode,
}: {
  value: ReactNode;
  copyOptions: CopyOption[];
  label?: string | undefined;
  mode: "visible" | "reveal";
}) {
  const [open, setOpen] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | undefined>(undefined);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleCopy = useCallback(
    (text: string, index: number) => {
      if (!text) return;
      clearTimeout(timeoutRef.current);
      copyToClipboard(text, label);
      setCopiedIndex(index);
      timeoutRef.current = setTimeout(() => {
        setOpen(false);
        setCopiedIndex(undefined);
      }, 800);
    },
    [label],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <span
          role="button"
          tabIndex={0}
          className={`inline-flex cursor-pointer items-center gap-1 rounded transition-colors hover:text-blue-text ${
            mode === "reveal" ? "group" : ""
          } ${open ? "text-blue-text" : ""}`}
        >
          {value}
          <span
            className={
              mode === "visible"
                ? "text-content-faint"
                : `overflow-hidden transition-all duration-150 ${open ? "w-4 opacity-100" : "w-0 opacity-0 group-hover:w-4 group-hover:opacity-100"}`
            }
          >
            <CopyIcon className="h-3 w-3" />
          </span>
        </span>
      </Popover.Trigger>
      <Popover.Content
        padding="sm"
        onInteractOutside={() => {
          setOpen(false);
          setCopiedIndex(undefined);
          clearTimeout(timeoutRef.current);
        }}
      >
        {copyOptions.map((option, i) => (
          <CopyButton_
            key={option.label}
            label={option.label}
            copied={copiedIndex === i}
            onClick={() => handleCopy(option.value, i)}
          />
        ))}
      </Popover.Content>
    </Popover>
  );
}

function CopyButton_({
  label,
  copied,
  onClick,
}: {
  label: string;
  copied: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium whitespace-nowrap text-foreground transition-colors hover:bg-muted"
    >
      {copied ? (
        <CheckIcon className="h-3.5 w-3.5 text-dark-green" weight="bold" />
      ) : (
        <CopyIcon className="h-3.5 w-3.5 text-content-faint" />
      )}
      {label}
    </button>
  );
}
